import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { getAllCreatorRequests, updateCreatorRequest } from '../api';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Clock, User, Mail, Phone, Link as LinkIcon, Loader } from 'lucide-react';

export const AdminCreatorsPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await getAllCreatorRequests(filter);
      setRequests(res.data.requests || []);
    } catch {
      toast.error('Erreur chargement demandes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); }, [filter]);

  const handleUpdate = async (requestId, newStatus) => {
    if (!window.confirm(`${newStatus === 'approved' ? 'Approuver' : 'Rejeter'} cette demande ?`)) return;
    setProcessing(true);
    try {
      await updateCreatorRequest(requestId, newStatus, adminNotes);
      toast.success(`Demande ${newStatus === 'approved' ? 'approuvée' : 'rejetée'}`);
      setSelectedRequest(null);
      setAdminNotes('');
      fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setProcessing(false);
    }
  };

  const statusBadge = (status) => {
    if (status === 'approved') return <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full flex items-center gap-1 w-fit"><CheckCircle className="w-3 h-3" />Approuvé</span>;
    if (status === 'rejected') return <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full flex items-center gap-1 w-fit"><XCircle className="w-3 h-3" />Rejeté</span>;
    return <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full flex items-center gap-1 w-fit"><Clock className="w-3 h-3" />En attente</span>;
  };

  const FILTERS = [
    { value: 'pending', label: 'En attente' },
    { value: 'approved', label: 'Approuvées' },
    { value: 'rejected', label: 'Rejetées' },
    { value: '', label: 'Toutes' },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-foreground">Demandes créateurs</h1>

        {/* Filtres */}
        <div className="flex gap-2 flex-wrap">
          {FILTERS.map(f => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === f.value ? 'bg-primary text-primary-foreground' : 'bg-card border border-border text-foreground hover:bg-accent'}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-20 flex justify-center"><Loader className="w-6 h-6 animate-spin text-primary" /></div>
        ) : requests.length === 0 ? (
          <div className="py-20 text-center bg-card border border-border rounded-xl text-muted-foreground">Aucune demande</div>
        ) : (
          <div className="space-y-4">
            {requests.map(req => (
              <div key={req.id} className="bg-card border border-border rounded-xl p-5 hover:border-primary/40 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                      <User className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground">{req.username}</h3>
                      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground mt-1">
                        <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{req.email}</span>
                        {req.phone_number && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{req.phone_number}</span>}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        {statusBadge(req.status)}
                        {req.content_type && <span className="px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded-full">{req.content_type}</span>}
                      </div>
                    </div>
                  </div>
                  {req.status === 'pending' && (
                    <button
                      onClick={() => setSelectedRequest(selectedRequest?.id === req.id ? null : req)}
                      className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors shrink-0"
                    >
                      {selectedRequest?.id === req.id ? 'Fermer' : 'Examiner'}
                    </button>
                  )}
                </div>

                {(selectedRequest?.id === req.id || req.status !== 'pending') && (
                  <div className="border-t border-border mt-4 pt-4 space-y-3">
                    {req.motivation && (
                      <div>
                        <p className="text-xs font-medium text-foreground mb-1">Motivation</p>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">{req.motivation}</p>
                      </div>
                    )}
                    {req.portfolio_url && (
                      <div>
                        <p className="text-xs font-medium text-foreground mb-1">Portfolio</p>
                        <a href={req.portfolio_url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline flex items-center gap-1">
                          <LinkIcon className="w-3 h-3" />{req.portfolio_url}
                        </a>
                      </div>
                    )}
                    {req.admin_notes && (
                      <div className="bg-muted/50 p-3 rounded-lg">
                        <p className="text-xs font-medium text-foreground mb-1">Note admin</p>
                        <p className="text-sm text-muted-foreground">{req.admin_notes}</p>
                      </div>
                    )}
                    {req.status === 'pending' && selectedRequest?.id === req.id && (
                      <div className="space-y-3 pt-2">
                        <textarea
                          className="w-full px-4 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                          rows={2}
                          placeholder="Note admin (optionnel)..."
                          value={adminNotes}
                          onChange={e => setAdminNotes(e.target.value)}
                        />
                        <div className="flex gap-3">
                          <button
                            onClick={() => handleUpdate(req.id, 'rejected')}
                            disabled={processing}
                            className="flex items-center gap-2 px-4 py-2 bg-red-500/10 text-red-400 rounded-lg text-sm hover:bg-red-500/20 transition-colors disabled:opacity-50"
                          >
                            <XCircle className="w-4 h-4" /> Rejeter
                          </button>
                          <button
                            onClick={() => handleUpdate(req.id, 'approved')}
                            disabled={processing}
                            className="flex items-center gap-2 px-4 py-2 bg-green-500/10 text-green-400 rounded-lg text-sm hover:bg-green-500/20 transition-colors disabled:opacity-50"
                          >
                            <CheckCircle className="w-4 h-4" /> Approuver
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
